/**
 * Winner certificate: a 1080×1350 PNG drawn on a canvas in the active theme.
 * Avatars are deliberately not drawn — TikTok CDN images are cross-origin and
 * would taint the canvas, which blocks PNG export. Initials are used instead.
 */
import { downloadBlob, initials, slug, themeVar } from './ui/dom.js';

const W = 1080;
const H = 1350;
const EMOJI = "'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji'";
const DISPLAY = `'Outfit Variable', 'Noto Sans Myanmar', system-ui, sans-serif, ${EMOJI}`;
const BODY = `'Inter Variable', 'Noto Sans Myanmar', system-ui, sans-serif, ${EMOJI}`;

function segments(text, granularity) {
  if (typeof Intl.Segmenter !== 'function') return granularity === 'word' ? text.split(/(\s+)/) : [...text];
  // 'my' enables dictionary-based word breaking for Burmese, which has no spaces.
  const parts = [...new Intl.Segmenter('my', { granularity }).segment(text)].map((s) => s.segment);
  if (granularity !== 'word') return parts;
  // Keep "#giveaway" and "@user" together instead of breaking after the symbol.
  const merged = [];
  for (const part of parts) {
    const prev = merged[merged.length - 1];
    if (prev && /[#@]$/.test(prev) && !/^\s/.test(part)) merged[merged.length - 1] = prev + part;
    else merged.push(part);
  }
  return merged;
}

/** Wraps text into at most `maxLines` lines no wider than `maxWidth`; the last line gets "…" if cut. */
export function wrapText(ctx, text, maxWidth, maxLines) {
  const fits = (s) => ctx.measureText(s).width <= maxWidth;
  const lines = [];
  let line = '';

  for (const piece of segments(text, 'word')) {
    if (fits(line + piece)) {
      line += piece;
      continue;
    }
    if (line.trim()) lines.push(line.trimEnd());
    line = '';
    const word = piece.trimStart();
    if (fits(word)) {
      line = word;
      continue;
    }
    // A single word wider than the line: break it by grapheme.
    for (const g of segments(word, 'grapheme')) {
      if (line && !fits(line + g)) {
        lines.push(line);
        line = '';
      }
      line += g;
    }
  }
  if (line.trim()) lines.push(line.trimEnd());

  if (lines.length <= maxLines) return lines;
  const kept = lines.slice(0, maxLines);
  const last = segments(kept[maxLines - 1], 'grapheme');
  while (last.length && !fits(`${last.join('').trimEnd()}…`)) last.pop();
  kept[maxLines - 1] = `${last.join('').trimEnd()}…`;
  return kept;
}

function fitFont(ctx, text, weight, family, startPx, minPx, maxWidth) {
  let px = startPx;
  for (; px > minPx; px -= 2) {
    ctx.font = `${weight} ${px}px ${family}`;
    if (ctx.measureText(text).width <= maxWidth) return px;
  }
  ctx.font = `${weight} ${minPx}px ${family}`;
  return minPx;
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
}

export async function ensureFonts(sample) {
  if (!document.fonts?.load) return;
  const loads = [
    `800 96px 'Outfit Variable'`,
    `700 64px 'Outfit Variable'`,
    `500 36px 'Inter Variable'`,
    `400 36px 'Noto Sans Myanmar'`,
    `700 64px 'Noto Sans Myanmar'`,
  ].map((font) => document.fonts.load(font, sample).catch(() => []));
  await Promise.all(loads);
  await document.fonts.ready;
}

export async function renderCertificate(winner, { videoId, host = '' } = {}) {
  const name = winner.displayName || winner.username;
  await ensureFonts(`${name} ${winner.comment} ${host} WINNER @${winner.username}`);

  const t = {
    bg: themeVar('--tk-bg') || '#0b0d12',
    bg2: themeVar('--tk-bg-2') || '#151a26',
    text: themeVar('--tk-text') || '#ffffff',
    muted: themeVar('--tk-muted') || '#999999',
    accent: themeVar('--tk-accent') || '#7c9cff',
    accent2: themeVar('--tk-accent-2') || '#b38cff',
    accentFg: themeVar('--tk-accent-fg') || '#0b0d12',
  };

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';

  // Background
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, t.bg2);
  bg.addColorStop(1, t.bg);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);
  for (const [x, y, r, color] of [
    [140, 120, 520, t.accent],
    [W - 120, H - 160, 560, t.accent2],
  ]) {
    const glow = ctx.createRadialGradient(x, y, 0, x, y, r);
    glow.addColorStop(0, `${color}55`);
    glow.addColorStop(1, `${color}00`);
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, W, H);
  }
  // Frame: one gradient line keeps the focus on the winner.
  const frame = ctx.createLinearGradient(0, 0, W, H);
  frame.addColorStop(0, t.accent);
  frame.addColorStop(1, t.accent2);
  ctx.strokeStyle = frame;
  ctx.lineWidth = 4;
  roundRect(ctx, 48, 48, W - 96, H - 96, 40);
  ctx.stroke();

  // Whose giveaway this was
  ctx.fillStyle = t.muted;
  fitFont(ctx, host || 'TokDraw giveaway', 600, BODY, 32, 22, 860);
  ctx.fillText(wrapText(ctx, host || 'TokDraw giveaway', 860, 1)[0], W / 2, 158);

  ctx.font = `110px ${EMOJI}`;
  ctx.fillText('🏆', W / 2, 288);

  const title = ctx.createLinearGradient(W / 2 - 260, 0, W / 2 + 260, 0);
  title.addColorStop(0, t.accent);
  title.addColorStop(1, t.accent2);
  ctx.fillStyle = title;
  ctx.font = `800 104px ${DISPLAY}`;
  ctx.letterSpacing = '12px';
  ctx.fillText('WINNER', W / 2 + 6, 410);
  ctx.letterSpacing = '0px';

  // Tier pill
  const tierText = [winner.tierName, winner.prize].filter(Boolean).join('  —  ');
  fitFont(ctx, tierText, 600, BODY, 30, 20, 760);
  const pillW = Math.min(840, ctx.measureText(tierText).width + 64);
  ctx.fillStyle = `${t.accent}2e`;
  roundRect(ctx, (W - pillW) / 2, 446, pillW, 58, 29);
  ctx.fill();
  ctx.strokeStyle = `${t.accent}80`;
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.fillStyle = t.text;
  ctx.fillText(tierText, W / 2, 485);

  // Monogram
  const cy = 625;
  const mono = ctx.createLinearGradient(W / 2 - 80, cy - 80, W / 2 + 80, cy + 80);
  mono.addColorStop(0, t.accent);
  mono.addColorStop(1, t.accent2);
  ctx.beginPath();
  ctx.arc(W / 2, cy, 80, 0, Math.PI * 2);
  ctx.fillStyle = mono;
  ctx.shadowColor = t.accent;
  ctx.shadowBlur = 40;
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.fillStyle = t.accentFg;
  ctx.font = `800 62px ${DISPLAY}`;
  ctx.textBaseline = 'middle';
  ctx.fillText(initials(name), W / 2, cy + 4);
  ctx.textBaseline = 'alphabetic';

  // Name + handle
  ctx.fillStyle = t.text;
  fitFont(ctx, name, 700, DISPLAY, 72, 40, 880);
  const shown = wrapText(ctx, name, 880, 1)[0];
  ctx.fillText(shown, W / 2, 786);
  ctx.fillStyle = t.accent2;
  ctx.font = `600 36px ${BODY}`;
  ctx.fillText(wrapText(ctx, `@${winner.username}`, 880, 1)[0], W / 2, 840);

  // Comment: a card sized to the text, centred in the space above the footer (as on the reveal).
  ctx.font = `400 38px ${BODY}`;
  const lines = wrapText(ctx, `“${winner.comment}”`, 720, 4);
  const lineHeight = 56;
  const cardW = Math.min(840, Math.max(...lines.map((line) => ctx.measureText(line).width)) + 112);
  const cardH = lines.length * lineHeight + 60;
  const cardTop = 880 + (1180 - 880 - cardH) / 2;
  ctx.fillStyle = `${t.text}0f`;
  roundRect(ctx, (W - cardW) / 2, cardTop, cardW, cardH, 28);
  ctx.fill();
  ctx.strokeStyle = `${t.text}1f`;
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.fillStyle = t.text;
  lines.forEach((line, i) => ctx.fillText(line, W / 2, cardTop + 30 + lineHeight * i + 40));

  // Footer
  const drawn = new Date(winner.drawnAt);
  const when = new Intl.DateTimeFormat(undefined, { dateStyle: 'long', timeStyle: 'short' }).format(drawn);
  ctx.fillStyle = t.muted;
  ctx.font = `500 26px ${BODY}`;
  ctx.fillText([when, videoId ? `Video ${videoId}` : ''].filter(Boolean).join('  ·  '), W / 2, 1230);
  ctx.font = `600 24px ${BODY}`;
  ctx.fillStyle = t.accent;
  ctx.fillText('Drawn fairly with TokDraw', W / 2, 1270);

  return canvas;
}

export async function downloadCertificate(winner, options) {
  const canvas = await renderCertificate(winner, options);
  const blob = await new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Could not create the PNG.'))), 'image/png'),
  );
  downloadBlob(blob, `tokdraw-winner-${slug(winner.username, 'winner')}.png`);
}
